import {useEffect,useState,Suspense} from "react";
import {Box,TextField,InputAdornment,Button,Tooltip} from '@mui/material';
import {DefaultConfig,evaluateReadOnly,consoleLog} from '../../../core/utilities/Utilities';
import dayjs from 'dayjs';
import {getPickListPopup} from '../../../custom/entities/PickListComponent.js';
import LoadingFallback from '../LoadingFallback.js';
const readOnlyBackgroundColor=DefaultConfig.readOnlyBackgroundColor;

const FieldText=({row,fieldName,application,recordRules,readOnly,setFieldValue,setFieldChanged,userData})=>{
    const fieldProp=row?.[fieldName];
    const fieldValue=fieldProp?.value;
    const readOnlyEvaluated=evaluateReadOnly(readOnly,recordRules,fieldProp);
    const [inputValue,setInputValue]=useState(fieldValue??"");
    const [isFieldChanged,setIsFieldChanged]=useState(false);
    const [isPickListPopupOpen,setIsPickListPopupOpen]=useState(false);
    const pickListPopupName=fieldProp?.ui_render_component_popup||null;
    const fieldTextColor=fieldProp?.ui_text_color||null;
    const PickListPopup=getPickListPopup(pickListPopupName);

    const handleOpenPickListPopup=()=>{
        if(pickListPopupName){
            setIsPickListPopupOpen(true);
        }
        else{
            consoleLog({file:'FieldText.js',context:'handleOpenPickListPopup',arg_name:'error',arg_value:'pickListPopup not defined'});
        }
    }

    const pickListCallback=(event)=>{
        if(event.name==='close_popup'){
            setIsPickListPopupOpen(false);
        }
        else if(event.name==='selectRow'){
            if(!isFieldChanged){setIsFieldChanged(true)}
            setIsPickListPopupOpen(false);
            setFieldChanged();
            setInputValue(event?.value);
            setFieldValue(fieldName,event?.value)
        }
    }

    useEffect(()=>{
      setIsFieldChanged(false);
      setInputValue(fieldValue??"");
    },[fieldValue]);

    const uiType=fieldProp?.ui_type;
    const isIntegerOnly=uiType==="number"&&!!fieldProp?.integer;
    const locale=application.globalData.locale||"en";
    const format=
        uiType==="datetime-coda"
        ?"L LT"
        :uiType==="date-coda"
        ?"L"
        :null;

    // inputValue can legitimately be 0 (e.g. sort_order) - `&&`/`||` treat that
    // as falsy and would blank the field out, so this checks presence instead
    // of truthiness.
    const hasInputValue=inputValue!==null&&inputValue!==undefined&&inputValue!=='';
    const value=hasInputValue&&format?dayjs(inputValue).locale(locale).format(format):(inputValue??"");

    return (
        <>
        {isPickListPopupOpen&&
            <Suspense
                fallback={<LoadingFallback/>}>
                <PickListPopup pickListCallback={pickListCallback} application={application} userData={userData} row={row}/>
            </Suspense>}
            <Box sx={{flex:1,minWidth:0}}>
                <Tooltip
                    placement="top"
                    title={fieldProp?.ui_tooltip?application.getLocale(fieldProp?.ui_tooltip):''}
                    disableHoverListener={!fieldProp?.ui_tooltip}>
                    <TextField
                        InputProps={{
                            readOnly:readOnlyEvaluated,
                            endAdornment:(
                                !readOnly&&pickListPopupName&&
                                <InputAdornment position="end">
                                    <Button
                                        variant="contained"
                                        size="small"
                                        color="primary"
                                        onClick={(e)=>handleOpenPickListPopup()}>{inputValue?application.getLocale("ButtonUpdate"):application.getLocale("ButtonSelect")}
                                    </Button>
                                </InputAdornment>
                            )
                        }}
                        sx={{
                            div:{backgroundColor:readOnlyEvaluated?readOnlyBackgroundColor:""},
                            input:fieldTextColor?{color:fieldTextColor}:undefined}}
                        style={{marginTop:"0px",marginBottom:"8px"}}
                        id={fieldName}
                        name={fieldName}
                        type={isIntegerOnly?"text":fieldProp?.ui_type}
                        required={fieldProp?.required?fieldProp?.required:false}
                        error={fieldProp?.validation_error?fieldProp?.validation_error:false}
                        helperText={fieldProp?.validation_error_message?fieldProp?.validation_error_message:''}
                        variant="outlined"
                        size="small"
                        label={fieldProp?.ui_label_locale?application.getLocale(fieldProp?.ui_label_locale):fieldName}
                        value={value}
                        margin="normal"
                        fullWidth
                        inputProps={isIntegerOnly?{inputMode:'numeric',pattern:'-?[0-9]*'}:undefined}
                        onChange={(e)=>{
                            if(!isFieldChanged){setIsFieldChanged(true)}
                            let newValue=e.target.value;
                            if(isIntegerOnly){
                                // Strip decimal separators ('.'/',') and any stray characters so an
                                // integer-only DB column (e.g. width/height/length) can't receive a
                                // decimal value that would fail on upsert.
                                newValue=newValue.replace(/[^\d-]/g,'').replace(/(?!^)-/g,'');
                            }
                            setInputValue(newValue);
                            setFieldChanged();
                        }}
                        onBlur={(e)=>{
                            if(isFieldChanged){
                                setFieldValue(fieldName,inputValue)?.then((data)=>{
                                    const committedValue=data?.record?.single_fields?.[fieldName]?.value;
                                    if(committedValue!==undefined){
                                        setInputValue(committedValue??"");
                                    }
                                });
                            }
                        }}/>
                </Tooltip>
            </Box>
        </>
    );
}

export default FieldText;
